import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { TextField, CircularProgress } from '@mui/material';
import { brandRegister } from '../../services/authService';
import { BrandRegisterRequest } from '../../types/authModel';

// MUI internals (.MuiOutlinedInput-root, .MuiInputLabel-root, etc.) aren't reachable
// via plain Tailwind classes, so this stays as an sx object — everything else below
// has been converted to Tailwind utilities.
const inputSx = {
  '& .MuiOutlinedInput-root': {
    background: '#faf8f5', borderRadius: '12px !important', fontSize: 14, fontFamily: 'DM Sans,sans-serif',
    '& fieldset': { borderColor: 'var(--border)' },
    '&:hover fieldset': { borderColor: '#d4c4b0' },
    '&.Mui-focused fieldset': { borderColor: 'var(--brand)' },
  },
  '& .MuiInputLabel-root': { fontSize: 14, fontFamily: 'DM Sans,sans-serif', color: 'var(--text-muted)' },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--brand)' },
};

const Register: React.FC = () => {
  const [form, setForm] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    mobile: '',
    gst: '',
    password: '',
    billingAddress: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // GST is optional — omit it from the payload rather than sending
      // an empty string when the user leaves the field blank.
      const payload: BrandRegisterRequest = {
        companyName: form.companyName,
        contactPerson: form.contactPerson,
        email: form.email,
        mobile: form.mobile,
        billingAddress: form.billingAddress,
        password: form.password,
        ...(form.gst.trim() && { gst: form.gst.trim() }),
      };

      await brandRegister(payload);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2200);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center p-6">
      <div className="w-full max-w-[520px]">
        <div className="text-center mb-7">
          <Link to="/login" className="inline-flex items-center justify-center no-underline mb-6">
            <img src="/adrojlogo.png" alt="Adroj Logo" className="h-[85px] w-auto object-contain" />
          </Link>
        </div>

        <div className="bg-white border border-[var(--border)] rounded-[20px] p-9 shadow-[var(--shadow-md)]">
          {success ? (
            <div className="text-center py-5">
              <div className="w-[60px] h-[60px] rounded-full bg-[var(--success-bg)] inline-flex items-center justify-center mb-4 text-[28px]">
                ✅
              </div>
              <h3 className="text-[var(--success)] font-bold text-xl mb-2">Registration Successful!</h3>
              <p className="text-[var(--text-muted)] text-sm">Please verify your email. Redirecting to login...</p>
            </div>
          ) : (
            <>
              <h2 className="text-[var(--text-primary)] font-bold text-[22px] mb-1 tracking-[-0.02em]">
                Create Account
              </h2>
              <p className="text-[var(--text-muted)] text-[13px] mb-6">
                Register your company to start booking ad spaces
              </p>

              {error && (
                <div className="bg-[var(--danger-bg,#fdecea)] border border-[var(--danger,#d32f2f)]/30 text-[var(--danger,#d32f2f)] text-[13px] rounded-[10px] px-3.5 py-2.5 mb-4">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
                {[
                  { name: 'companyName', label: 'Company Name' },
                  { name: 'contactPerson', label: 'Contact Person' },
                  { name: 'email', label: 'Email Address', type: 'email' },
                  { name: 'mobile', label: 'Mobile Number', type: 'tel' },
                  { name: 'gst', label: 'GST Number (optional)', required: false },
                  { name: 'billingAddress', label: 'Billing Address' },
                  { name: 'password', label: 'Password', type: 'password' },
                ].map((f) => (
                  <TextField
                    key={f.name}
                    name={f.name}
                    label={f.label}
                    type={f.type || 'text'}
                    value={(form as any)[f.name]}
                    onChange={handleChange}
                    required={f.required !== false}
                    fullWidth
                    sx={inputSx}
                    size="small"
                  />
                ))}
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-brand p-[13px] text-sm mt-1 flex items-center justify-center gap-2"
                >
                  {loading ? <CircularProgress size={18} color="inherit" /> : 'Create Account'}
                </button>
                <p className="text-center text-[var(--text-muted)] text-[13px] m-0">
                  Already have an account?{' '}
                  <Link to="/login" className="text-[var(--brand)] font-semibold no-underline">
                    Sign in
                  </Link>
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;